import React, { createContext, useContext, useState, useEffect } from "react";

const RestaurantContext = createContext();

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error("useRestaurant must be used within RestaurantProvider");
  }
  return context;
};

export const RestaurantProvider = ({ children }) => {
  const [restaurantSettings, setRestaurantSettings] = useState({
    openingTime: "13:00",
    closingTime: "23:00",
    isOpen: true,
    timezone: "Asia/Kolkata",
    isCurrentlyOpen: true,
    weeklyHours: [],
    todayHours: { isOpen: true, openingTime: "13:00", closingTime: "23:00" },
  });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

  // Fetch restaurant settings
  const fetchSettings = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/settings`);
      const data = await response.json();

      if (data.success) {
        setRestaurantSettings(data.data);
      }
    } catch (error) {
      console.error("Error fetching restaurant settings:", error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Update restaurant settings (admin only)
  const updateSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem("gutful_token");
      const response = await fetch(`${API_URL}/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newSettings),
      });

      const data = await response.json();

      if (data.success) {
        setRestaurantSettings(data.data);
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error("Error updating restaurant settings:", error);
      return { success: false, message: "Error updating settings" };
    }
  };

  // Check if restaurant is currently open
  const isRestaurantOpen = () => {
    return restaurantSettings.isCurrentlyOpen && restaurantSettings.isOpen;
  };

  // Format time for display (convert 24h to 12h format)
  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  // Get opening/closing message
  const getStatusMessage = () => {
    if (!restaurantSettings.isOpen) {
      return "Restaurant is temporarily closed";
    }

    if (isRestaurantOpen()) {
      return `Open until ${formatTime(restaurantSettings.closingTime)}`;
    } else {
      return `Closed - Opens at ${formatTime(restaurantSettings.openingTime)}`;
    }
  };

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  //Refresh settings every minute to update open/closed status
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSettings();
    }, 60000 * 30); // 30 minutes

    return () => clearInterval(interval);
  }, [fetchSettings]);

  const value = {
    restaurantSettings,
    loading,
    isRestaurantOpen,
    updateSettings,
    formatTime,
    getStatusMessage,
    refreshSettings: fetchSettings,
  };

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};
